import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Storefront, ShieldCheck, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, FilterBar, FilterChip, StatusBadge, BulkActionBar } from '../../components/admin/AdminComponents';
import { DeleteConfirmModal } from '../../components/DeleteConfirmModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminShops() {
  const [shops, setShops] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [vFilter, setVFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async ({ reason, hardDelete }) => {
    if (!deleteTarget) return;
    try {
      const url = `${API_URL}/api/admin/deletion/shops/${deleteTarget.id}${hardDelete ? '?force=true' : ''}`;
      await axios.delete(url, { data: { confirm: true, reason }, withCredentials: true });
      toast.success(`Shop ${hardDelete ? 'permanently deleted' : 'deactivated'}`);
      setDeleteTarget(null);
      fetchShops();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const fetchShops = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (vFilter) params.append('verified', vFilter);
      params.append('skip', p * 50); params.append('limit', '50');
      const { data } = await axios.get(`${API_URL}/api/admin-console/shops?${params}`, { withCredentials: true });
      setShops(data.shops || []); setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchShops(); }, [vFilter, page]);

  const verify = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/shops/${id}/verify`, {}, { withCredentials: true });
      toast.success('Shop verified'); fetchShops();
    } catch { toast.error('Failed'); }
  };

  const unverify = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/shops/${id}/unverify`, {}, { withCredentials: true });
      toast.success('Verification revoked'); fetchShops();
    } catch { toast.error('Failed'); }
  };

  const bulkVerify = async () => {
    try {
      await axios.post(`${API_URL}/api/admin-console/bulk/verify-shops`, { ids: selected }, { withCredentials: true });
      toast.success(`Verified ${selected.length} shops`); setSelected([]); fetchShops();
    } catch { toast.error('Failed'); }
  };

  const columns = [
    {
      key: 'name', label: 'Shop',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Storefront size={16} weight="duotone" className="text-[#F59E0B]" />
          <div>
            <p className="text-[13px] text-white font-medium">{row.name}</p>
            <p className="text-[10px] text-[#444]">{row.slug}</p>
          </div>
        </div>
      ),
    },
    { key: 'location', label: 'Location', cellClass: 'hidden md:table-cell', render: v => <span className="text-[12px] text-[#666]">{v || '—'}</span> },
    {
      key: 'verified', label: 'Status',
      render: v => v ? (
        <span className="flex items-center gap-1 text-[11px] text-[#10B981]"><ShieldCheck size={13} weight="fill" /> Verified</span>
      ) : <StatusBadge status="pending" size="xs" />,
    },
    { key: 'reputation_tier', label: 'Tier', cellClass: 'hidden lg:table-cell', render: v => <StatusBadge status={v || 'bronze'} size="xs" /> },
    { key: 'member_count', label: 'Members', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[12px] text-[#666] tabular-nums">{v}</span> },
    { key: 'plan', label: 'Plan', cellClass: 'hidden xl:table-cell', render: v => <StatusBadge status={v || 'free'} size="xs" /> },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
          {!row.verified ? (
            <button onClick={() => verify(row.id)}
              className="text-[11px] px-2.5 py-1 rounded-md bg-[#10B981]/10 text-[#10B981] font-medium hover:bg-[#10B981]/20"
              data-testid={`verify-shop-${row.id}`}>Verify</button>
          ) : (
            <button onClick={() => unverify(row.id)}
              className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 text-[#555] font-medium hover:text-white"
              data-testid={`unverify-shop-${row.id}`}>Revoke</button>
          )}
          {!row.is_deleted && (
            <button onClick={() => setDeleteTarget(row)}
              className="text-[11px] px-2 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] font-medium hover:bg-[#EF4444]/20 inline-flex items-center gap-1"
              data-testid={`delete-shop-${row.id}`}>
              <Trash size={11} weight="bold" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-shops-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Shops</h1>
        <p className="text-[13px] text-[#444] mt-0.5">{total} registered shops</p>
      </div>

      <FilterBar searchValue={search} onSearchChange={setSearch} onSearch={() => { setPage(0); fetchShops(0); }}
        searchPlaceholder="Search shops..." count={total} label="shops">
        {[['', 'All'], ['true', 'Verified'], ['false', 'Unverified']].map(([v, l]) => (
          <FilterChip key={v} label={l} active={vFilter === v}
            onClick={() => { setVFilter(v); setPage(0); }} testId={`filter-verified-${v || 'all'}`} />
        ))}
      </FilterBar>

      <BulkActionBar count={selected.length} onClear={() => setSelected([])}
        actions={[{ label: 'Bulk Verify', color: '#10B981', onClick: bulkVerify }]}
      />

      <DataTable columns={columns} data={shops} loading={loading} emptyMessage="No shops found" emptyIcon={Storefront}
        selectable selectedIds={selected} onSelectionChange={setSelected} rowTestIdPrefix="shop"
        page={page} pageSize={50} total={total} onPageChange={setPage}
      />

      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        targetType="shop"
        targetName={deleteTarget?.name}
        warning="This will deactivate the shop, hide it from discovery, cancel active events, and disable shop portal access. Past events, recaps, and stats are preserved."
        allowHardDelete={true}
      />
    </div>
  );
}
